import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetClose } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CloseButton, useIsMobile } from "@/components/shared/PagePrimitives";
import { useDetailNav, DetailNav } from "@/components/shared/ListNav";
import { useDockEscape } from "@/components/shared/DetailDock";
import { PropRow, PROP_TRIGGER, PropSelect, propHotkey } from "@/components/shared/PropRow";
import { ReceiptCards } from "@/components/shared/DocPartyCards";
import { Preview, projectOptions, catOptions } from "@/components/shared/DocumentReviewDialog";
import { DocTagEyebrow } from "@/components/economics/DocTags";
import { formatMoneyExact as formatCZK, formatDate } from "@/lib/format";
import { AmountTiles, AmountTile } from "@/components/shared/AmountTiles";
import { pendingCurrency } from "@/lib/documents";
import { Banknote, Briefcase, CheckCircle2, ChevronDown, CreditCard, ReceiptText, Shapes, Trash2 } from "lucide-react";
import { autoPill } from "@/components/shared/Pill";

// Detail účtenky (229) — 372 podle varianty B jako faktura: nahoře obchod, čtyři dlaždice (Bez DPH, DPH,
// Celkem, Zaplaceno), vlastnosti jako řádky (Zakázka — Z, Kategorie, Platba), rozbalitelné položky
// a údaje z účtenky jako karty Obchod a Doklad (tužka → pole). Fotka účtenky po tlačítku „Účtenka“
// vlevo (na telefonu záložka), čerstvý sken ji ukáže rovnou. Řízené rodičem: stav `rec`, uložení, smazání.

const PAY = ["Kartou", "Hotovost"];

// 343: `nav` = { index, count, onGo } — účtenka otevřená ze seznamu dokladů: „2 / 5", ↑ ↓, mezerník zavře.
// 370: `docked` = panel vedle tabulky jako faktura (Ekonomika › Doklady na PC); rodič ho obalí DetailDock.
// 372: `onFileShown(bool)` — ukázaná fotka v panelu schová tabulku; `canTag` + `onManageTags` — štítek nad obchodem.
export function ReceiptDetail({ open, onOpenChange, rec, setRec, isNew, cats, projects, hint, banner, busy, blocked, onSave, onSaveEdit, onDelete, onDiscard, onShowExisting, nav,
  docked = false, onFileShown, canTag = false, onManageTags }) {
  const mobile = useIsMobile();
  const [editShop, setEditShop] = useState(false);
  const [editHead, setEditHead] = useState(false);
  const [itemsOpen, setItemsOpen] = useState(false);
  const [tab, setTab] = useState("data");            // telefon: Údaje / Účtenka
  const [showFile, setShowFile] = useState(false);   // PC: fotka vlevo
  const [pick, setPick] = useState(null);
  useEffect(() => { if (open) { setEditShop(false); setEditHead(false); setItemsOpen(false); setTab("data"); setPick(null); } }, [open, rec?.id]);
  useEffect(() => { if (open) setShowFile(!!isNew && !!rec?.file_url); }, [open, !!isNew]); // eslint-disable-line react-hooks/exhaustive-deps
  const recNav = nav && rec?.id && !isNew ? nav : null;
  useDetailNav(recNav, open, () => onOpenChange(false), { dialogs: docked ? 0 : 1, letters: false });
  useDockEscape(() => onOpenChange(false), docked && !!open && !!rec);
  const cardRef = useRef(null);
  useEffect(() => { if (docked && open) cardRef.current?.focus?.({ preventScroll: true }); }, [docked, open]);
  const fileShown = !!(open && rec?.file_url && showFile && !mobile);
  useEffect(() => { if (docked) onFileShown?.(fileShown); }, [docked, fileShown]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (docked) onFileShown?.(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Z otevře výběr zakázky, K kategorie (jako u faktury)
  const zOn = !!open && !!rec && !mobile;
  useEffect(() => {
    if (!zOn) return undefined;
    const onZ = propHotkey("z", () => setPick("prj")), onK = propHotkey("k", () => setPick("cat"));
    const onKey = (e) => { onZ(e); onK(e); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [zOn]);
  if (!rec) return null;
  const editing = isNew || editShop || editHead;
  const set = (k, v) => setRec({ ...rec, [k]: v });
  const fm = (v) => formatCZK(v, pendingCurrency(rec));
  const items = rec.items || [];
  const itemsSum = items.reduce((t, it) => t + (Number(it.line_total) || (Number(it.quantity) || 1) * (Number(it.unit_price) || 0)), 0);
  const vatRates = (rec.vat_breakdown || []).filter((x) => x && x.rate != null);
  const letter = (rec.supplier_name || "?").trim().charAt(0).toUpperCase();
  const total = Number(rec.total) || 0, vat = Number(rec.vat_amount) || 0, net = Math.round((total - vat) * 100) / 100;
  const pct = net > 0 && vat > 0 ? Math.round((vat / net) * 100) : null;
  const rate = vatRates.length > 1 ? null : [12, 21].includes(pct) ? pct : null;
  const paidHow = rec.payment_method === "Hotovost" ? "Hotově" : rec.payment_method || "";
  const fileBtn = rec.file_url && !mobile && (
    <Button type="button" variant="outline" className="h-10 rounded-full" onClick={() => setShowFile((s) => !s)} aria-pressed={showFile} data-testid="receipt-show-file">
      <ReceiptText size={16} /> Účtenka</Button>
  );

  const header = (
    <div className="shrink-0 flex items-center gap-2 px-4 md:px-5 pt-4 pb-2">
      <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-bold tabular-nums" data-testid="receipt-no">
        {rec.internal_no ? `UC ${rec.internal_no} · ` : ""}Účtenka</span>
      {!showFile && fileBtn}
      <span className="flex-1" />
      <DetailNav nav={recNav} testid="receipt-nav" />
      {docked ? <CloseButton testid="receipt-close" onClick={() => onOpenChange(false)} title="Zavřít (mezerník / Esc)" /> : <SheetClose asChild><CloseButton testid="receipt-close" /></SheetClose>}
    </div>
  );

  const body = (
    <div className="space-y-5">
      {hint}
      {banner}
      <div>
        <DocTagEyebrow value={rec.tag || null} onChange={(t) => set("tag", t)} onManage={onManageTags} mobile={mobile} readOnly={!canTag}
          hotkey={!!open} testid="receipt-tag" />
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-full bg-success/15 text-success grid place-items-center font-heading font-extrabold text-lg shrink-0" aria-hidden>{letter}</span>
          <div className="min-w-0">
            <h2 className="font-heading font-extrabold text-[22px] md:text-[26px] tracking-tight leading-tight break-words" data-testid="receipt-supplier">{rec.supplier_name || "Účtenka"}</h2>
            <p className="text-sm text-muted-foreground truncate">{[rec.address, rec.receipt_number && `účtenka č. ${rec.receipt_number}`].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
      </div>

      <AmountTiles testid="receipt-amounts">
        <AmountTile label="Bez DPH" value={fm(net)} />
        <AmountTile label={rate ? `DPH ${rate} %` : "DPH"} value={fm(vat)} sub={vatRates.length > 1 ? vatRates.map((x) => `${x.rate} %: ${formatCZK(x.vat)}`).join(" · ") : null} />
        <AmountTile label="Celkem s DPH" value={fm(total)} testid="receipt-total" />
        <AmountTile label="Zaplaceno" tone="success" value={paidHow || "—"} sub={rec.date ? formatDate(rec.date) : null} testid="receipt-paid" />
      </AmountTiles>

      <section className="space-y-0.5" data-testid="receipt-props">
        <PropRow label="Zakázka" kbd={mobile ? null : "Z"}>
<PropSelect open={pick === "prj"} onOpenChange={(o) => setPick(o ? "prj" : null)} mobile={mobile} title="Zakázka" icon={Briefcase}
            valueLabel={rec.project_id ? (projects.find((p) => p.id === rec.project_id)?.name || rec.project_name || "Zakázka") : "Režie (bez zakázky)"}
            options={projectOptions(projects, rec.project_id || null, "Režie (bez zakázky)")} onPick={(v) => set("project_id", v)}
            search placeholder="Hledat zakázku, číslo, adresu…" testid="receipt-pick-proj" triggerTestid="receipt-project" />
        </PropRow>
        <PropRow label={<>Kategorie <span className="text-destructive">*</span></>} kbd={mobile ? null : "K"}>
          <PropSelect open={pick === "cat"} onOpenChange={(o) => setPick(o ? "cat" : null)} mobile={mobile} title="Kategorie" icon={Shapes} width={260}
            valueLabel={rec.category || <span className="text-muted-foreground">Vyberte</span>} options={catOptions(cats, rec.category || null)} onPick={(v) => set("category", v)}
            search={cats.length > 9} placeholder="Hledat kategorii…" testid="receipt-pick-cat" triggerTestid="receipt-category" />
        </PropRow>
        <PropRow label="Platba">
          <Select value={rec.payment_method || undefined} onValueChange={(v) => set("payment_method", v)}>
            <SelectTrigger className={PROP_TRIGGER} data-testid="receipt-payment" aria-label="Platba">
              {rec.payment_method === "Hotovost" ? <Banknote size={16} className="text-muted-foreground shrink-0" /> : <CreditCard size={16} className="text-muted-foreground shrink-0" />}
              <span className="truncate"><SelectValue placeholder="Vyberte" /></span>
            </SelectTrigger>
            <SelectContent>
              {PAY.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              {rec.payment_method && !PAY.includes(rec.payment_method) && <SelectItem value={rec.payment_method}>{rec.payment_method}</SelectItem>}
            </SelectContent>
          </Select>
        </PropRow>
      </section>

      <section>
        <button type="button" onClick={() => setItemsOpen((o) => !o)} aria-expanded={itemsOpen} data-testid="receipt-items-toggle"
          className="w-full rounded-[16px] bg-secondary/60 flex items-center gap-2 px-3.5 min-h-[52px] text-left">
          <span className="font-semibold text-sm">Položky</span>
          <span className="rounded-full bg-card px-2 text-xs font-bold tabular-nums">{items.length}</span>
          <span className="flex-1" />
          {items.length > 0 && <span className="text-sm text-muted-foreground tabular-nums">{formatCZK(itemsSum)}</span>}
          <ChevronDown size={17} className={`text-muted-foreground transition-transform motion-reduce:transition-none ${itemsOpen ? "rotate-180" : ""}`} />
        </button>
        {itemsOpen && (
          <div className="mt-1 rounded-[16px] border border-border divide-y divide-border" data-testid="receipt-items">
            {items.length === 0 && <div className="p-3 text-sm text-muted-foreground">Žádné rozpoznané položky.</div>}
            {items.map((it, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-3.5 py-2.5 text-sm">
                <span className="min-w-0 truncate">{it.description || "Položka"}</span>
                <span className="text-muted-foreground shrink-0 tabular-nums">{it.quantity && Number(it.quantity) !== 1 ? `${Number(it.quantity).toLocaleString("cs-CZ")} × ` : ""}{formatCZK(it.unit_price)}</span>
              </div>
            ))}
            {items.length > 0 && Math.abs(itemsSum - (Number(rec.total) || 0)) > 0.5 && (
              <div className="px-3.5 py-2.5 text-[13px] text-amber-700 dark:text-amber-400">Součet položek {formatCZK(itemsSum)} nesedí s celkem {formatCZK(rec.total)} — zkontrolujte slevy a haléřové vyrovnání.</div>
            )}
          </div>
        )}
      </section>

      <section data-testid="receipt-details">
        <h3 className="text-sm font-semibold text-muted-foreground mb-2">Údaje z účtenky</h3>
        <ReceiptCards rec={rec} set={set} setRec={setRec} isNew={isNew} editShop={editShop} setEditShop={setEditShop} editHead={editHead} setEditHead={setEditHead} />
        <p className="text-[13px] text-muted-foreground mt-2" data-testid={rec.file_url ? undefined : "receipt-no-file"}>
          {rec.created_at ? `Nahráno ${formatDate(rec.created_at)}${rec.created_by_name ? ` · ${rec.created_by_name}` : ""}` : ""}
          {!rec.file_url ? `${rec.created_at ? " · " : ""}bez přílohy — doklad zadán ručně` : ""}
        </p>
      </section>
    </div>
  );

  const footer = (
    <div className={`shrink-0 border-t border-border bg-background/95 backdrop-blur px-4 md:px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex gap-2 ${isNew ? "flex-col-reverse md:flex-row" : "flex-row items-center"}`}>
      {isNew ? (
        <>
          <Button onClick={onDiscard} variant="outline" data-testid="receipt-cancel" className="w-full md:w-auto h-11 font-semibold">Zahodit</Button>
          <span className="hidden md:block flex-1" />
          {blocked ? (
            <Button variant="outline" onClick={onShowExisting} data-testid="receipt-show-existing" className="w-full md:w-auto h-11 font-bold px-6">Zobrazit existující</Button>
          ) : (
            <Button onClick={onSave} disabled={busy} data-testid="receipt-save" className="w-full md:w-auto h-11 font-bold px-6"><CheckCircle2 size={18} /> Uložit účtenku</Button>
          )}
        </>
      ) : (
        <>
          <Button onClick={onDelete} variant="outline" aria-label="Smazat účtenku" data-testid="receipt-delete" className="w-11 md:w-auto px-0 md:px-4 h-11 shrink-0 font-semibold text-destructive hover:text-destructive"><Trash2 size={17} /><span className="hidden md:inline">Smazat</span></Button>
          <span className="hidden md:block flex-1" />
          <Button onClick={() => onSaveEdit(editing)} disabled={busy} data-testid="receipt-save-edit" className="flex-1 md:flex-none h-11 font-bold px-6"><CheckCircle2 size={18} /> Uložit změny</Button>
        </>
      )}
    </div>
  );

  const photo = <Preview doc={rec} onHide={() => setShowFile(false)} />;
  // PC: fotka vlevo (po tlačítku), údaje vpravo; telefon: záložky Údaje / Účtenka
  const inner = mobile ? (
    <>
      {header}
      {rec.file_url && (
        <div className="px-4 pb-2 shrink-0">
          <div ref={autoPill} className="grid grid-cols-2 gap-1 p-1 rounded-full bg-secondary" role="tablist">
            {[["data", "Údaje"], ["file", "Účtenka"]].map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`receipt-tab-${k}`}
                className={`h-9 rounded-full text-sm font-semibold ${tab === k ? "bg-card shadow-sm" : "text-muted-foreground"}`}>{l}</button>
            ))}
          </div>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {tab === "file" && rec.file_url ? photo : <div className="px-4 pb-6">{body}</div>}
      </div>
      {footer}
    </>
  ) : (
    <>
      {fileShown && <div className="w-[42%] max-w-[520px] shrink-0 bg-secondary/60 border-r border-border min-h-0" data-testid="receipt-image-pane">{photo}</div>}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        {header}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 md:px-5 pb-6">{body}</div>
        {footer}
      </div>
    </>
  );

  // 370: panel vedle tabulky (rodič ho obalí DetailDock)
  if (docked) {
    if (!open) return null;
    return (
      <div ref={cardRef} tabIndex={-1} data-testid="receipt-review-dialog" aria-label={isNew ? "Kontrola účtenky" : "Detail účtenky"}
        className="h-full flex bg-card border border-border rounded-[24px] overflow-hidden shadow-[0_12px_40px_rgba(0,0,0,.10)] outline-none">
        {inner}
      </div>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* 344: na PC kurzor do okna — mezerník a šipky pak patří detailu */}
      <SheetContent side={mobile ? "bottom" : "right"} hideClose data-testid="receipt-review-dialog"
        onOpenAutoFocus={(e) => { e.preventDefault(); if (!mobile) e.currentTarget?.focus?.({ preventScroll: true }); }}
        className={mobile ? "p-0 h-[94dvh] rounded-t-[24px] flex flex-col pb-0"
          : `p-0 border-0 md:inset-y-3 md:right-3 md:h-auto md:rounded-[24px] overflow-hidden flex flex-row sm:max-w-none ${fileShown ? "w-[min(1180px,calc(100vw-24px))]" : "w-[min(720px,calc(100vw-24px))]"}`}>
        <SheetTitle className="sr-only">{isNew ? "Kontrola účtenky" : "Detail účtenky"}</SheetTitle>
        <SheetDescription className="sr-only">Účtenka</SheetDescription>
        {mobile && <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-border shrink-0" aria-hidden />}
        {inner}
      </SheetContent>
    </Sheet>
  );
}
